import React, { useState, useEffect, useMemo, useRef } from "react";
import SectionTitle from "../components/SectionTitle";
import Seo from "../components/Seo";
import { useData } from "../context/DataContext";
import api from "../lib/api";
import { toast } from "sonner";
import {
  CheckCircle2, ArrowRight, ArrowLeft,
  Loader2, HelpCircle,
} from "lucide-react";

import Step1Family from "./inscription/Step1Family";
import Step2Students from "./inscription/Step2Students";
import Step3Payment from "./inscription/Step3Payment";
import Step4Done from "./inscription/Step4Done";
import LevelHelpModal from "./inscription/LevelHelpModal";

const STEPS = ["Famille", "Élèves & créneaux", "Paiement", "Confirmation"];

const Inscription = () => {
  const { scheduleSlots, settings } = useData();
  const [step, setStep] = useState(0);
  const [stripeEnabled, setStripeEnabled] = useState(true);
  const [capacity, setCapacity] = useState({});
  const wizardTopRef = useRef(null);
  const isInitialMount = useRef(true);

  // Scroll wizard back to the top of the current step after each transition (after DOM commit).
  // Skip on initial mount so the user still sees the hero when landing on /inscription.
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (wizardTopRef.current) {
      wizardTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [step]);

  const [family, setFamily] = useState({
    parentFirstName: "", parentLastName: "",
    email: "", phone: "",
    address: "", postalCode: "", city: "",
    password: "",
  });
  const [students, setStudents] = useState([
    { firstName: "", lastName: "", birthDate: "", slotIds: [] },
  ]);
  const [paymentMode, setPaymentMode] = useState("cheque");
  const [message, setMessage] = useState("");
  const [consents, setConsents] = useState({ cgv: false, reglement: false, image: false, whatsapp: false, cseAttestation: false });

  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [, setSubmitted] = useState(null);

  const [showLevelHelp, setShowLevelHelp] = useState(false);

  useEffect(() => {
    api.get("/payments/config").then(({ data }) => setStripeEnabled(!!data.stripeEnabled)).catch(() => setStripeEnabled(false));
    api.get("/slots/capacity").then(({ data }) => setCapacity(data || {})).catch(() => {});
  }, []);

  const totalSlotsSelected = useMemo(
    () => students.reduce((s, st) => s + (st.slotIds?.length || 0), 0),
    [students]
  );

  useEffect(() => {
    if (step < 2) return;
    if (totalSlotsSelected === 0) { setQuote(null); return; }
    setQuoteLoading(true);
    api.post("/inscriptions/quote", { students, postalCode: family.postalCode })
      .then(({ data }) => setQuote(data))
      .catch(() => toast.error("Erreur lors du calcul du tarif"))
      .finally(() => setQuoteLoading(false));
  }, [step, students, totalSlotsSelected, family.postalCode]);

  const validateStep = () => {
    if (step === 0) {
      if (!family.parentFirstName.trim()) return "Veuillez renseigner le prénom du responsable.";
      if (!family.parentLastName.trim()) return "Veuillez renseigner le nom du responsable.";
      if (!family.email.trim()) return "Veuillez renseigner l'email.";
      if (!family.postalCode || !/^[0-9]{5}$/.test(family.postalCode.trim())) return "Veuillez renseigner un code postal valide (5 chiffres).";
      if (!family.city.trim()) return "Veuillez renseigner la ville.";
      if (!family.password || family.password.length < 6) return "Le mot de passe doit faire au moins 6 caractères.";
      return null;
    }
    if (step === 1) {
      if (students.length === 0) return "Ajoutez au moins un élève.";
      for (const st of students) {
        if (!st.firstName.trim()) return "Chaque élève doit avoir un prénom.";
        if (!st.lastName.trim()) return "Chaque élève doit avoir un nom de famille.";
        if (!st.birthDate) return `Renseignez la date de naissance de ${st.firstName || "l'élève"}.`;
        if ((st.slotIds || []).length === 0) return `Sélectionnez au moins un créneau pour ${st.firstName || "l'élève"}.`;
      }
      return null;
    }
    if (step === 2) {
      if (!paymentMode) return "Choisissez un mode de paiement.";
      if (!consents.cgv || !consents.reglement) {
        return "Merci d'accepter les CGV et le règlement intérieur.";
      }
      return null;
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => {
    setStep((s) => Math.max(s - 1, 0));
  };

  const addStudent = () => setStudents([...students, { firstName: "", lastName: "", birthDate: "", slotIds: [] }]);
  const removeStudent = (i) => setStudents(students.filter((_, idx) => idx !== i));
  const updateStudent = (i, patch) => setStudents(students.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const toggleSlot = (i, slotId) => {
    const st = students[i];
    const ids = st.slotIds || [];
    const has = ids.includes(slotId);
    updateStudent(i, { slotIds: has ? ids.filter((c) => c !== slotId) : [...ids, slotId] });
  };

  const submit = async () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...family,
        parentName: `${family.parentFirstName} ${family.parentLastName}`.trim(),
        paymentMode, students, message, consents,
      };
      const { data } = await api.post("/inscriptions/submit", payload);
      setSubmitted(data);
      if (paymentMode === "card" || paymentMode === "monthly") {
        if (!stripeEnabled) {
          toast.error("Paiement en ligne indisponible. L'inscription est enregistrée, nous vous contacterons.");
          setStep(3);
          return;
        }
        try {
          const checkout = await api.post("/payments/checkout", {
            enrollmentIds: data.enrollmentIds,
            originUrl: window.location.origin,
            mode: paymentMode,
          });
          // Save retry context so PaymentCancel can offer "Réessayer" / "Passer en manuel"
          try {
            sessionStorage.setItem("elan_stripe_retry", JSON.stringify({
              enrollmentIds: data.enrollmentIds,
              orderNumber: data.orderNumber || "",
              orderId: data.orderId || "",
              mode: paymentMode,
              total: data.total,
              parentEmail: family.email,
              parentName: `${family.parentFirstName} ${family.parentLastName}`.trim(),
              savedAt: Date.now(),
            }));
          } catch { /* sessionStorage may be blocked, ignore */ }
          window.location.href = checkout.data.url;
          return;
        } catch (payErr) {
          const payDetail = payErr?.response?.data?.detail;
          toast.error(typeof payDetail === "string" && payDetail
            ? payDetail
            : "Erreur lors de la création du paiement. L'inscription a été enregistrée.");
        }
      }
      setStep(3);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur lors de l'enregistrement");
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <Seo title="Inscription — L'Élan Danse" description="Inscrivez votre famille pour la saison à l'école de danse L'Élan Danse au Perray-en-Yvelines." />

      <section className="py-12 sm:py-16 px-4 sm:px-6">
        <SectionTitle kicker="Rejoignez-nous" title="Inscription" subtitle={`Saison ${settings?.season || "2026/2027"} · Remplissez les étapes ci-dessous pour inscrire votre famille.`} />

        <div className="max-w-4xl mx-auto mb-8">
          <div className="bg-coral text-white rounded-2xl p-5 sm:p-6 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                <HelpCircle size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="inline-block bg-white text-coral font-bold text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full">Étape obligatoire</span>
                  <h3 className="font-serif-display text-xl sm:text-2xl">Toute nouvelle inscription débute ici</h3>
                </div>
                <p className="text-white/95 text-sm sm:text-base mt-2 leading-relaxed">
                  <strong>Avant de remplir le formulaire ci-dessous</strong>, merci de nous contacter via la demande de conseil. Nous échangerons avec vous pour identifier le <strong>cours et le niveau adaptés</strong> à votre enfant, puis nous vous indiquerons quand et comment finaliser votre inscription.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <button onClick={() => setShowLevelHelp(true)} data-testid="open-level-help-btn" className="inline-flex items-center justify-center gap-2 bg-white text-coral px-5 py-2.5 rounded-full text-sm tracking-widest uppercase font-medium hover:bg-blush-light transition-colors">
                    Demander conseil <ArrowRight size={14} />
                  </button>
                  <span className="text-white/80 text-xs sm:text-sm self-center italic">Déjà été contacté(e) par l&apos;école&nbsp;? Vous pouvez remplir le formulaire ci-dessous.</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {showLevelHelp && <LevelHelpModal onClose={() => setShowLevelHelp(false)} />}

        <div ref={wizardTopRef} className="max-w-4xl mx-auto mb-8 scroll-mt-24">
          <div className="flex items-center justify-between gap-2 px-2">
            {STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <div className="flex flex-col items-center gap-1.5 min-w-0">
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    i < step ? "bg-deep-rose text-white" : i === step ? "bg-coral text-white" : "bg-stone-200 text-stone-500"
                  }`}>
                    {i < step ? <CheckCircle2 size={18} /> : i + 1}
                  </div>
                  <span className={`text-[10px] sm:text-xs uppercase tracking-widest text-center ${i === step ? "text-deep-rose font-medium" : "text-stone-500"}`}>{s}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`h-px flex-1 ${i < step ? "bg-deep-rose" : "bg-stone-200"}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="max-w-3xl mx-auto bg-white rounded-3xl shadow-sm p-6 sm:p-10">
          {step === 0 && <Step1Family family={family} setFamily={setFamily} />}
          {step === 1 && (
            <Step2Students students={students} updateStudent={updateStudent} addStudent={addStudent} removeStudent={removeStudent}
              toggleSlot={toggleSlot} scheduleSlots={scheduleSlots} capacity={capacity} />
          )}
          {step === 2 && (
            <Step3Payment paymentMode={paymentMode} setPaymentMode={setPaymentMode}
              message={message} setMessage={setMessage}
              consents={consents} setConsents={setConsents}
              quote={quote} quoteLoading={quoteLoading}
              settings={settings} stripeEnabled={stripeEnabled} />
          )}
          {step === 3 && <Step4Done paymentMode={paymentMode} family={family} />}

          {step < 3 && (
            <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 mt-8 pt-6 border-t border-stone-100">
              <button onClick={back} disabled={step === 0} data-testid="step-back-btn" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-stone-100 text-stone-700 disabled:opacity-30 disabled:cursor-not-allowed">
                <ArrowLeft size={16} /> Retour
              </button>
              {step < 2 ? (
                <button onClick={next} data-testid="step-next-btn" className="inline-flex items-center justify-center gap-2 bg-coral text-white px-6 py-2.5 rounded-full tracking-widest uppercase text-sm">
                  Continuer <ArrowRight size={16} />
                </button>
              ) : (
                <button onClick={submit} disabled={submitting || totalSlotsSelected === 0 || !consents.cgv || !consents.reglement} data-testid="step-submit-btn" className="inline-flex items-center justify-center gap-2 bg-coral text-white px-6 py-2.5 rounded-full tracking-widest uppercase text-sm disabled:opacity-50">
                  {submitting ? (<><Loader2 className="animate-spin" size={16}/> Validation...</>) : (<>Valider l&apos;inscription <ArrowRight size={16} /></>)}
                </button>
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Inscription;
